/**
 * This file is part of the Haiman.
 * @link     : https://haiman.io/
 * @author   : Emil Zhai (root@derzh.com)
 * @modifier : Emil Zhai (root@derzh.com)
 * @copyright: Copyright (c) 2018 Hangzhou Haila Network Technology Co., Ltd.
 */

import { CHAT } from '@/store/types';
import * as api from '@/api/chat';
import { TIMMessage } from '@/api/types/chat';
import { StoreActionEnv } from '@/store/actions';
import { chatLogin } from '@/utils/authorization';

export interface ChatConversationDetailData {
  cid: string;
  list: TIMMessage[];
  point: string;
  limit: number;
  lock: boolean;
  noMore: boolean;
}

export interface StoreChatConversationDetailState {
  cache: ChatConversationDetailData[];
  maxCache: number;
  current: ChatConversationDetailData;
}

const getDefault = (): ChatConversationDetailData => ({
  cid: '',
  list: [],
  point: '',
  limit: 15,
  lock: false,
  noMore: false,
});

export default {
  namespaced: true,
  state: {
    cache: [],
    maxCache: 20,
    current: getDefault(),
  },
  actions: {
    [CHAT.GET_HISTORY](
      { state, commit }: StoreActionEnv<StoreChatConversationDetailState>,
      { cid, limit = 15, reload, more }: { cid: string; limit?: number; reload?: boolean; more?: boolean },
    ) {
      commit(CHAT.SEEK_CACHE, { cid });
      if (state.current.cid !== cid) {
        reload = true;
      }
      if (reload || more) {
        commit(CHAT.GET_HISTORY, { status: 'start', cid });
        return new Promise((resolve, reject) => {
          chatLogin().then(({ appid }) => {
            const point = reload ? 0 : state.current.point;
            api.getMessageList(appid, cid, limit, point).then((res) => {
              commit(CHAT.GET_HISTORY, {
                status: 'success',
                mode: 'prepend',
                cid,
                list: res.data,
                noMore: !res.point,
                point: res.point,
              });
              resolve();
            }).catch(reject);
          }).catch(reject);
        });
      }
      return Promise.resolve();
    },
    [CHAT.SEND_PICTURE](
      { commit }: StoreActionEnv<StoreChatConversationDetailState>,
      { cid, file, onProgress }: { cid: string; file: HTMLInputElement | object; onProgress: (per: number) => void },
    ) {
      return new Promise((resolve, reject) => {
        chatLogin().then(({ appid }) => {
          api.sendImageMessage(
            appid,
            cid,
            file,
            onProgress,
          ).then((res) => {
            commit(CHAT.GET_HISTORY, {
              status: 'success',
              mode: 'append',
              cid,
              list: [res.data],
            });
            resolve();
          }).catch(reject);
        }).catch(reject);
      });
    },
    [CHAT.SEND_TEXT]({ commit }, { cid, text }) {
      return new Promise((resolve, reject) => {
        chatLogin().then(({ appid }) => {
          api.sendTextMessage(
            appid,
            cid,
            text,
          ).then((res) => {
            commit(CHAT.GET_HISTORY, {
              status: 'success',
              mode: 'append',
              cid,
              list: [res.data],
            });
            resolve();
          }).catch(reject);
        }).catch(reject);
      });
    },
  },
  mutations: {
    [CHAT.SEEK_CACHE](state, { cid }) {
      if (state.current.cid === cid) {
        return;
      }
      const cache = state.cache.find(p => p.cid === cid);
      if (!cache) {
        return;
      }
      state.current = cache;
    },
    [CHAT.GET_HISTORY](state: StoreChatConversationDetailState, { status, mode, list, noMore, point, cid }) {
      const index = state.cache.findIndex(p => p.cid === cid);
      const cache = index >= 0 ? state.cache[index] : getDefault();
      if (index === -1) {
        while (state.cache.length > state.maxCache) {
          state.cache.pop();
        }
        state.cache.unshift(cache);
      } else if (index > 0) {
        state.cache.splice(index, 1);
        state.cache.unshift(cache);
      }
      if (status === 'start') {
        cache.lock = true;
      } else if (status === 'success') {
        cache.list = cache.list.filter(p => list.some(q => p.id !== q.id));
        if (mode === 'prepend') {
          cache.cid = cid;
          cache.list = list.concat(cache.list);
          cache.noMore = noMore;
          cache.point = point;
          state.current = cache;
        } else if (mode === 'append') {
          cache.list = cache.list.concat(list);
        }
        cache.list = cache.list.sort((a, b) => a.time - b.time);
        cache.lock = false;
      }
    },
  },
};
